import { Popup } from "@progress/kendo-react-popup";
import classNames from "classnames";
import { KeyRoundIcon } from "lucide-react";
import React, { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { If, Then } from "react-if";
import { useNavigate } from "react-router-dom";
import { useSlidePanelState } from "@/components/slide-panel";
import { Success } from "@/components/Snackbar";
import {
  TRUSTLOGIX_DOCS_URL,
  TRUSTLOGIX_RELEASE_NOTES_URL,
} from "@/configs/constants";
import { URLs } from "@/configs/menu";
import { useAuthority, useUserState } from "@/context/UserContext";
import {
  ThemePreference,
  getThemePreference,
  setThemePreference as applyThemePreference,
} from "@/helpers/theme";
import useDialogState from "@/hooks/useDialogState";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import MFAIcon from "@/icons/MFA.svg?react";
import QuestionIcon from "@/icons/question-circle.svg?react";
import UserIcon from "@/icons/user.svg?react";
import MFA from "@/layout/MFA";
import ReleaseUpdates, { ReleasePanelProps } from "@/layout/ReleaseUpdates";
import { logOut } from "@/libs/store";
import ChangePasswordPanel from "@/pages/Users/ChangePasswordPanel";

import "./UserMenu.scss";

const themeOptions: ThemePreference[] = ["light", "dark", "system"];

type DocInterface = Omit<ReleasePanelProps, "onClose">;

export default function UserMenu() {
  const { user } = useUserState();
  const { hasAuthority } = useAuthority();
  const userName = user!.userName;
  const tenantName = user!.tenantName;
  const [open, setOpen] = React.useState(false);
  const [openHelp, setOpenHelp] = React.useState(false);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const helpRef = useRef<HTMLSpanElement>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const history = useNavigate();
  const { t } = useTranslation();

  // `isMounted` outlives `isOpen` by the slide-out, so the panel can animate
  // closed instead of vanishing.
  const {
    isOpen: isPasswordOpen,
    isMounted: isPasswordMounted,
    open: openPassword,
    close: closePassword,
  } = useSlidePanelState();
  const [doc /* , setDoc */] = React.useState<DocInterface>({} as DocInterface);
  const [isHelp, setIsHelp] = React.useState<boolean>(false);
  const [successMessage, setSuccess] = React.useState<string | null>(null);
  const [themePreference, setThemePreferenceState] =
    React.useState<ThemePreference>(getThemePreference);
  const [isMFA, openMFA, closeMFA] = useDialogState(false);

  React.useEffect(() => {
    if (open) {
      setThemePreferenceState(getThemePreference());
    }
  }, [open]);

  const handleToggle = (event?: React.MouseEvent) => {
    // Prevent the trigger's native `href="#"` navigation, which otherwise
    // pushes a fragment to history and causes a page blink/flicker on click.
    event?.preventDefault();
    setOpen(prevOpen => !prevOpen);
  };

  const handleHelpToggle = () => {
    setOpenHelp(prevOpen => !prevOpen);
  };

  const handleClose = useCallback(
    (event?: React.MouseEvent<EventTarget>) => {
      if (event && anchorRef.current?.contains(event.target as HTMLElement)) {
        return;
      }

      if (event) {
        const { id } = event.target as HTMLElement;
        setOpen(false);
        switch (id) {
          case "logout":
            return logOut();
          case "user":
            void history(URLs.users);
            break;
          case "MFA":
            return openMFA();
          default:
            // handle other cases or do nothing
            break;
        }
      } else {
        setOpen(false);
      }
    },
    [history, openMFA],
  );

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  useOnClickOutside(helpMenuRef, () => setOpenHelp(false), {
    current: helpRef.current,
  });

  useOnClickOutside(userMenuRef, () => setOpen(false), {
    current: anchorRef.current,
  });

  const [name, lastname] = (userName || "").split(" ");

  const onClose = useCallback(() => {
    closePassword();
    setIsHelp(false);
  }, [closePassword]);

  const handleChangePassword = useCallback(
    (event: React.MouseEvent<EventTarget>) => {
      event.preventDefault();
      openPassword();
      void handleClose(event);
    },
    [handleClose, openPassword],
  );

  const handleThemeSelect = useCallback((preference: ThemePreference) => {
    applyThemePreference(preference);
    setThemePreferenceState(preference);
  }, []);

  return (
    <>
      <div className="tlx-user-menu" id="user-menu">
        <div className="tlx-user-menu__help-wrapper" id="help-toggle-wrapper">
          <span
            ref={helpRef}
            className="tlx-user-menu__help-icon"
            onClick={handleHelpToggle}
          >
            <QuestionIcon />
          </span>
        </div>

        <a
          aria-label="account of current user"
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          id="user-menu"
          className="tlx-user-menu__container"
          href="#"
          onMouseDown={e => e.preventDefault()}
        >
          <div className="tlx-user-menu__tenant-name" id="tenant-name">
            {tenantName}
          </div>
          <div className="tlx-user-menu__avatar">
            {name[0]}
            {lastname?.[0]}
          </div>
        </a>

        <Popup
          show={openHelp}
          anchor={helpRef.current}
          popupClass="tlx-user-menu__popup"
          anchorAlign={{ horizontal: "right", vertical: "bottom" }}
          popupAlign={{ horizontal: "right", vertical: "top" }}
          margin={{ horizontal: 0, vertical: 5 }}
        >
          <div ref={helpMenuRef}>
            <ul
              className="tlx-menu-list tlx-user-menu__list"
              id="help-list-grow"
              onKeyDown={handleListKeyDown}
            >
              <li>
                <a
                  href={TRUSTLOGIX_DOCS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="tlx-user-menu__item-link"
                >
                  <div className="tlx-user-menu__item">
                    {t("userMenu.helpMenuList.documentation")}
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={TRUSTLOGIX_RELEASE_NOTES_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="tlx-user-menu__item-link"
                >
                  <div className="tlx-user-menu__item">
                    {t("userMenu.helpMenuList.releaseNotes")}
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </Popup>

        <Popup
          show={open}
          anchor={anchorRef.current}
          popupClass="tlx-user-menu__popup"
          anchorAlign={{ horizontal: "left", vertical: "bottom" }}
          popupAlign={{ horizontal: "left", vertical: "top" }}
          // horizontal: 0 — a -50px offset used to drag the popup half a
          // hundred pixels left of the CTA, so its left edge never lined up with
          // the anchor despite the left/left alignment (QA-07).
          margin={{ horizontal: 0, vertical: 5 }}
        >
          <div ref={userMenuRef}>
            <ul
              className="tlx-menu-list tlx-user-menu__list"
              id="menu-list-grow"
              onKeyDown={handleListKeyDown}
            >
              <li>
                <div
                  id="user"
                  onClick={handleClose}
                  className={classNames("tlx-user-menu__item", {
                    "tlx-user-menu__item--disabled":
                      !hasAuthority("USERS:READ"),
                  })}
                >
                  <div className="tlx-user-menu__item-icon">
                    <UserIcon style={{ width: 20, height: 20 }} />
                  </div>
                  {userName}
                </div>
              </li>
              <If condition={user?.skipSso || !user?.isTenantFederated}>
                <Then>
                  <li>
                    <div
                      id="changePassword"
                      onClick={handleChangePassword}
                      className="tlx-user-menu__item"
                    >
                      <div className="tlx-user-menu__item-icon">
                        <KeyRoundIcon style={{ width: 20, height: 20 }} />
                      </div>
                      {t("changePassword.title")}
                    </div>
                  </li>
                </Then>
              </If>
              <If condition={user?.skipSso || !user?.isTenantFederated}>
                <Then>
                  <li>
                    <div
                      id="MFA"
                      onClick={handleClose}
                      className="tlx-user-menu__item"
                    >
                      <div className="tlx-user-menu__item-icon">
                        <MFAIcon style={{ width: 20, height: 20 }} />
                      </div>
                      Multi-Factor Authentication
                    </div>
                  </li>
                </Then>
              </If>
              <li>
                <div
                  id="logout"
                  onClick={handleClose}
                  className="tlx-user-menu__item"
                >
                  <div className="tlx-user-menu__item-icon">
                    <span className="k-icon k-font-icon k-i-logout" />
                  </div>
                  Logout
                </div>
              </li>
              <li>
                <div
                  className="tlx-user-menu__item tlx-user-menu__item--theme"
                  onClick={event => event.stopPropagation()}
                >
                  <div className="tlx-user-menu__item-icon">
                    <span className="k-icon k-font-icon k-i-brush" />
                  </div>
                  <div className="tlx-user-menu__theme-content">
                    <span>{t("userMenu.theme.label")}</span>
                    <div
                      className="tlx-user-menu__theme-toggle-group"
                      onClick={event => event.stopPropagation()}
                      onMouseDown={event => event.stopPropagation()}
                      onKeyDown={event => event.stopPropagation()}
                      role="group"
                      aria-label={t("userMenu.theme.label")}
                      data-test-id="theme-toggle-group"
                    >
                      {themeOptions.map(option => {
                        const isActive = themePreference === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => handleThemeSelect(option)}
                            data-test-id={`theme-toggle-${option}`}
                            aria-pressed={isActive}
                            className={classNames(
                              "tlx-user-menu__theme-button",
                              {
                                "tlx-user-menu__theme-button--active": isActive,
                              },
                            )}
                          >
                            {t(`userMenu.theme.${option}`)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </Popup>
      </div>
      <Success
        message={successMessage}
        onClose={() => setSuccess(null)}
        autoHideDuration={30000}
        title={t("users.title")}
      />
      {isPasswordMounted && (
        <ChangePasswordPanel
          open={isPasswordOpen}
          onClose={onClose}
          loginId={user?.userId}
          onSaved={() => {
            setSuccess(t("changePassword.success"));
            onClose();
          }}
        />
      )}
      {isHelp && <ReleaseUpdates {...doc} onClose={onClose} />}
      <MFA
        isOpen={isMFA}
        onClose={closeMFA}
        isMfaEnabled={user?.mfaEnabled!}
        mfaEnableDate={user?.mfaEnableDate}
      />
    </>
  );
}
